import axios from 'axios';
import { auth } from '../config/firebase';
import { OrderRequest, Store, Staff, Customer, Manufacturer, NewCustomer, CommitmentOrderData, CommitmentOrderUpdate, StaffLeave, DailyAccount, Task, TaskFilters, TaskStatus, CashPositionReport, BankDebitRow, ExpenseBreakdownRow } from '../types';

// Dynamic API URL based on current hostname
const getApiBaseUrl = () => {
  // If we have a configured API URL, use it
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // Otherwise, construct it based on current hostname
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  
  // Check if this is production domain
  if (hostname === 'oms.firstshield.in') {
    return `${protocol}//${hostname}/api`;
  }
  
  // Check if this is a Tailscale domain and use port 8443
  if (hostname.includes('.ts.net')) {
    return `${protocol}//${hostname}:8443/api`;
  }
  
  // Use port 5000 for local development
  return `${protocol}//${hostname}:5000/api`;
};

const API_BASE_URL = getApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Proof of sign-in for the backend: the Firebase ID token of the signed-in user (Firebase caches it
 * and refreshes it automatically before it expires), plus the email header older endpoints still read.
 */
export const getAuthHeaders = async (): Promise<Record<string, string>> => {
  const headers: Record<string, string> = {};
  const userEmail = localStorage.getItem('userEmail');
  if (userEmail) headers['x-user-email'] = userEmail;
  try {
    const user = auth.currentUser;
    if (user) headers['Authorization'] = `Bearer ${await user.getIdToken()}`;
  } catch { /* no token: the backend decides (report-only mode lets it through) */ }
  return headers;
};

// Add the sign-in token + user email to every API request
api.interceptors.request.use(async (config) => {
  const headers = await getAuthHeaders();
  Object.entries(headers).forEach(([k, v]) => { config.headers[k] = v; });
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Order requests
export interface OrderFilters {
  store_id?: string;
  date_from?: string;
  date_to?: string;
  customer_name?: string;
  customer_phone?: string;
  status?: string;
  medicine_name?: string;
}

export const orderAPI = {
  getAll: (filters?: OrderFilters): Promise<OrderRequest[]> => {
    let url = '/orders';
    if (filters) {
      const params = new URLSearchParams();
      if (filters.store_id) params.append('store_id', filters.store_id);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (filters.customer_name) params.append('customer_name', filters.customer_name);
      if (filters.customer_phone) params.append('customer_phone', filters.customer_phone);
      if (filters.status) params.append('status', filters.status);
      if (filters.medicine_name) params.append('medicine_name', filters.medicine_name);
      
      const queryString = params.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }
    return api.get(url).then(response => response.data);
  },
  
  getById: (id: number): Promise<OrderRequest> => 
    api.get(`/orders/${id}`).then(response => response.data),
  
  create: (order: Omit<OrderRequest, 'id'>): Promise<OrderRequest> => 
    api.post('/orders', order).then(response => response.data),
  
  updateStatus: (id: number, status: string, updated_by?: string): Promise<OrderRequest> => 
    api.patch(`/orders/${id}/status`, { status, updated_by }).then(response => response.data),
  
  updateMessageStatus: (id: number, message_status: string, updated_by?: string): Promise<OrderRequest> => 
    api.patch(`/orders/${id}/message-status`, { message_status, updated_by }).then(response => response.data),
  
  delete: (id: number): Promise<void> => 
    api.delete(`/orders/${id}`).then(response => response.data),
};

// Lookup data
export const lookupAPI = {
  getStores: (): Promise<Store[]> => 
    api.get('/lookup/stores').then(response => response.data),
  
  getStaff: (): Promise<Staff[]> => 
    api.get('/lookup/staff').then(response => response.data),
  
  getCustomers: (): Promise<Customer[]> => 
    api.get('/lookup/customers').then(response => response.data),
  
  searchCustomers: (query: string): Promise<Customer[]> => 
    api.get(`/lookup/customers/search?q=${encodeURIComponent(query)}`).then(response => response.data),
  
  createCustomer: (customer: NewCustomer): Promise<Customer> => 
    api.post('/lookup/customers', customer).then(response => response.data),
  
  getManufacturers: (): Promise<Manufacturer[]> => 
    api.get('/lookup/manufacturers').then(response => response.data),
  
  searchMedicines: (query: string, storeId: number): Promise<Manufacturer[]> => 
    api.get(`/lookup/medicines/search?q=${encodeURIComponent(query)}&store_id=${storeId}`).then(response => response.data),
  
  getManufacturerById: (id: number): Promise<Manufacturer> => 
    api.get(`/lookup/manufacturers/${id}`).then(response => response.data),
  
  createMedicine: (medicine: { medicine_name: string; manufacturer_name?: string; combination?: string; pack_size?: string; created_by?: string }): Promise<Manufacturer> => 
    api.post('/lookup/manufacturers', medicine).then(response => response.data),
  
  getManufacturerNames: (): Promise<string[]> => 
    api.get('/lookup/manufacturer-names').then(response => response.data),
  
  getMedicineInventory: (manufacturerId: number, storeId: number): Promise<{ quantity: number; title?: string; nc_order?: string }> => 
    api.get(`/lookup/inventory/${manufacturerId}/${storeId}`).then(response => response.data),
};

// Commitment orders
export const commitmentAPI = {
  getOrders: (filters: { store_id: string; date_from: string; date_to: string }): Promise<CommitmentOrderData> => {
    const params = new URLSearchParams();
    params.append('store_id', filters.store_id);
    params.append('date_from', filters.date_from);
    params.append('date_to', filters.date_to);
    
    return api.get(`/commitment/orders?${params.toString()}`).then(response => response.data);
  },
  
  updateOrders: (data: { store_id: string; date_from: string; date_to: string; items: CommitmentOrderUpdate[] }): Promise<CommitmentOrderData> => 
    api.post('/commitment/orders/update', data).then(response => response.data),
};

// Staff Leave API
export const leaveAPI = {
  getAll: (filters?: { date_from?: string; date_to?: string; staff_id?: number }): Promise<StaffLeave[]> => {
    let url = '/leave';
    if (filters) {
      const params = new URLSearchParams();
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (filters.staff_id) params.append('staff_id', filters.staff_id.toString());
      
      const queryString = params.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }
    return api.get(url).then(response => response.data);
  },
  
  getById: (id: number): Promise<StaffLeave> => 
    api.get(`/leave/${id}`).then(response => response.data),
  
  create: (leave: Omit<StaffLeave, 'id'>): Promise<StaffLeave> => 
    api.post('/leave', leave).then(response => response.data),
  
  update: (id: number, leave: Partial<StaffLeave>): Promise<StaffLeave> => 
    api.patch(`/leave/${id}`, leave).then(response => response.data),
  
  delete: (id: number): Promise<void> => 
    api.delete(`/leave/${id}`).then(response => response.data),
};

// Daily Account API
export const dailyAccountAPI = {
  getByDateAndStore: (date: string, storeId: number): Promise<DailyAccount | null> => 
    api.get(`/daily-account/by-date-store?date=${date}&store_id=${storeId}`).then(response => response.data),
  
  getAll: (filters?: { store_id?: string; date_from?: string; date_to?: string }): Promise<DailyAccount[]> => {
    let url = '/daily-account';
    if (filters) {
      const params = new URLSearchParams();
      if (filters.store_id) params.append('store_id', filters.store_id);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      
      const queryString = params.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }
    return api.get(url).then(response => response.data);
  },
  
  create: (account: Omit<DailyAccount, 'id'>): Promise<DailyAccount> => 
    api.post('/daily-account', account).then(response => response.data),
  
  update: (id: number, account: Partial<DailyAccount>): Promise<DailyAccount> => 
    api.put(`/daily-account/${id}`, account).then(response => response.data),
  
  delete: (id: number): Promise<void> => 
    api.delete(`/daily-account/${id}`).then(response => response.data),
  
  getCashFlowSummary: (filters: { store_id?: string; date_from: string; date_to: string }): Promise<any> => {
    const params = new URLSearchParams();
    if (filters.store_id && filters.store_id !== 'all') params.append('store_id', filters.store_id);
    params.append('date_from', filters.date_from);
    params.append('date_to', filters.date_to);
    
    return api.get(`/daily-account/cash-flow-summary?${params.toString()}`).then(response => response.data);
  },

  getCashPositionReport: (filters: { store_id?: string; date_from: string; date_to: string }): Promise<CashPositionReport> => {
    const params = new URLSearchParams();
    if (filters.store_id && filters.store_id !== 'all') params.append('store_id', filters.store_id);
    params.append('date_from', filters.date_from);
    params.append('date_to', filters.date_to);

    return api.get(`/daily-account/cash-position-report?${params.toString()}`).then(response => response.data);
  },

  getBankDebits: (filters: { store_id: number; date_from: string; date_to: string }): Promise<{ success: boolean; rows: BankDebitRow[] }> => {
    const params = new URLSearchParams();
    params.append('store_id', String(filters.store_id));
    params.append('date_from', filters.date_from);
    params.append('date_to', filters.date_to);

    return api.get(`/daily-account/bank-debits?${params.toString()}`).then(response => response.data);
  },

  getExpenseBreakdown: (filters: { store_id?: string; date_from: string; date_to: string }): Promise<{ success: boolean; items: ExpenseBreakdownRow[]; total: number }> => {
    const params = new URLSearchParams();
    if (filters.store_id && filters.store_id !== 'all') params.append('store_id', filters.store_id);
    params.append('date_from', filters.date_from);
    params.append('date_to', filters.date_to);

    return api.get(`/daily-account/expense-breakdown?${params.toString()}`).then(response => response.data);
  },
};

// Tasks API
export const tasksAPI = {
  getAll: (filters?: TaskFilters): Promise<Task[]> => {
    let url = '/tasks';
    if (filters) {
      const params = new URLSearchParams();
      if (filters.store_id) params.append('store_id', filters.store_id);
      if (filters.status) params.append('status', filters.status);
      if (filters.assignee_id) params.append('assignee_id', filters.assignee_id.toString());
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (filters.exclude_parents) params.append('exclude_parents', 'true');

      const queryString = params.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }
    return api.get(url).then(response => response.data);
  },

  getById: (id: number): Promise<Task> =>
    api.get(`/tasks/${id}`).then(response => response.data),

  getCalendarTasks: (year: number, month: number, storeId?: string): Promise<Task[]> => {
    let url = `/tasks/calendar/${year}/${month}`;
    if (storeId && storeId !== 'all') {
      url += `?store_id=${storeId}`;
    }
    return api.get(url).then(response => response.data);
  },

  getRecurringTasks: (storeId?: string): Promise<Task[]> => {
    let url = '/tasks/recurring';
    if (storeId && storeId !== 'all') {
      url += `?store_id=${storeId}`;
    }
    return api.get(url).then(response => response.data);
  },

  create: (task: Omit<Task, 'id'>): Promise<Task> =>
    api.post('/tasks', task).then(response => response.data),

  update: (id: number, task: Partial<Task>): Promise<Task> =>
    api.put(`/tasks/${id}`, task).then(response => response.data),

  updateStatus: (id: number, status: TaskStatus): Promise<Task> =>
    api.patch(`/tasks/${id}/status`, { status }).then(response => response.data),

  delete: (id: number): Promise<void> =>
    api.delete(`/tasks/${id}`).then(response => response.data),

  processRecurring: (): Promise<{ success: boolean; results: any[] }> =>
    api.post('/tasks/process-recurring').then(response => response.data),

  generateInstance: (parentId: number): Promise<{ success: boolean; instance?: Task; reason?: string }> =>
    api.post(`/tasks/${parentId}/generate-instance`).then(response => response.data),

  getInstances: (parentId: number): Promise<Task[]> =>
    api.get(`/tasks/${parentId}/instances`).then(response => response.data),

  updateTemplate: (instanceId: number, data: { title?: string; description?: string; notes?: string; assignee_id?: number; priority?: string }): Promise<{ success: boolean; message: string; template: Task }> =>
    api.patch(`/tasks/${instanceId}/update-template`, data).then(response => response.data),

  generateForDate: (targetDate: string, storeId?: string): Promise<{ success: boolean; totalGenerated: number; results: any[] }> =>
    api.post('/tasks/generate-for-date', { target_date: targetDate, store_id: storeId }).then(response => response.data),
};

// Health check
export const healthCheck = (): Promise<{ status: string; timestamp: string }> =>
  api.get('/health').then(response => response.data);

// evitalrx Purchase Payment Tracker
export const evitalrxAPI = {
  sync: (storeId: number | 'all', fromDate?: string, toDate?: string) =>
    api.post('/utilities/evitalrx-sync', {
      store_id: storeId,
      from_date: fromDate,
      to_date: toDate,
    }).then(r => r.data),

  getSummary: (storeId: number | 'all', status: 'due' | 'paid' | 'all' = 'due') => {
    const params = new URLSearchParams();
    if (storeId !== 'all') params.append('store_id', String(storeId));
    params.append('status', status);
    return api.get(`/utilities/evitalrx-purchases/summary?${params}`).then(r => r.data);
  },

  getPurchases: (filters: {
    store_id?: number | 'all';
    status?: 'due' | 'paid' | 'all';
    from_date?: string;
    to_date?: string;
    distributor_name?: string;
  }) => {
    const params = new URLSearchParams();
    if (filters.store_id && filters.store_id !== 'all') params.append('store_id', String(filters.store_id));
    if (filters.status) params.append('status', filters.status);
    if (filters.from_date) params.append('from_date', filters.from_date);
    if (filters.to_date) params.append('to_date', filters.to_date);
    if (filters.distributor_name) params.append('distributor_name', filters.distributor_name);
    return api.get(`/utilities/evitalrx-purchases?${params}`).then(r => r.data);
  },

  markPaid: (ids: number[], paymentMethod: string, paidBy: string) =>
    api.patch('/utilities/evitalrx-purchases/mark-paid', {
      ids,
      payment_method: paymentMethod,
      paid_by: paidBy,
    }).then(r => r.data),
};

// evitalrx Sales Tracker
export const evitalrxSalesAPI = {
  sync: (storeId: number | 'all', fromDate?: string, toDate?: string) =>
    api.post('/utilities/evitalrx-sales-sync', {
      store_id: storeId,
      from_date: fromDate,
      to_date: toDate,
    }).then(r => r.data),

  getDailySummary: (filters: { store_id?: number | 'all'; from_date?: string; to_date?: string }) => {
    const params = new URLSearchParams();
    if (filters.store_id && filters.store_id !== 'all') params.append('store_id', String(filters.store_id));
    if (filters.from_date) params.append('from_date', filters.from_date);
    if (filters.to_date) params.append('to_date', filters.to_date);
    return api.get(`/utilities/evitalrx-sales/daily-summary?${params}`).then(r => r.data);
  },

  getSales: (filters: {
    store_id?: number | 'all';
    from_date?: string;
    to_date?: string;
    entry_by?: string;
    payment_method?: string;
  }) => {
    const params = new URLSearchParams();
    if (filters.store_id && filters.store_id !== 'all') params.append('store_id', String(filters.store_id));
    if (filters.from_date) params.append('from_date', filters.from_date);
    if (filters.to_date) params.append('to_date', filters.to_date);
    if (filters.entry_by) params.append('entry_by', filters.entry_by);
    if (filters.payment_method) params.append('payment_method', filters.payment_method);
    return api.get(`/utilities/evitalrx-sales?${params}`).then(r => r.data);
  },
};

export const nonMovingItemsAPI = {
  getReport: (filters: {
    store_id: number; from_date: string; to_date: string;
    min_days_since_purchase?: number; include_zero_stock?: boolean;
  }) => {
    const params = new URLSearchParams();
    params.append('store_id', String(filters.store_id));
    params.append('from_date', filters.from_date);
    params.append('to_date', filters.to_date);
    if (filters.min_days_since_purchase !== undefined && !Number.isNaN(filters.min_days_since_purchase)) {
      params.append('min_days_since_purchase', String(filters.min_days_since_purchase));
    }
    if (filters.include_zero_stock) params.append('include_zero_stock', 'true');
    return api.get(`/utilities/non-moving-items?${params}`).then(r => r.data);
  },
};

export const discountOptimizerAPI = {
  getReport: (filters: {
    store_id: number;
    lookback_days?: number;
    protect_customers?: number;
    healthy_margin_pct?: number;
    big_bill_threshold?: number;
    big_bill_cap_pct?: number;
    band_near_disc?: number;
    band_mid_disc?: number;
    band_niche_disc?: number;
    include_protected?: boolean;
  }) => {
    const params = new URLSearchParams();
    params.append('store_id', String(filters.store_id));
    Object.entries(filters).forEach(([k, v]) => {
      if (k !== 'store_id' && v !== undefined && v !== null && !(typeof v === 'number' && Number.isNaN(v))) {
        params.append(k, String(v));
      }
    });
    return api.get(`/utilities/discount-optimizer?${params}`).then(r => r.data);
  },
};

// Monthly Customer Refills — derived refill list + per-customer basket
export const monthlyRefillsAPI = {
  getList: (filters: {
    store_id: number;
    lookback_months?: number;
    min_bill_amount?: number;
    min_months_qualifying?: number;
    min_months_item?: number;
    min_repeat_items?: number;
    completed_threshold_pct?: number;
    partial_threshold_pct?: number;
    churn_days?: number;
  }) => {
    const params = new URLSearchParams();
    params.append('store_id', String(filters.store_id));
    Object.entries(filters).forEach(([k, v]) => {
      if (k !== 'store_id' && v !== undefined && v !== null && !(typeof v === 'number' && Number.isNaN(v))) {
        params.append(k, String(v));
      }
    });
    return api.get(`/utilities/monthly-refills?${params}`).then(r => r.data);
  },

  getItems: (customerId: number, filters: {
    store_id: number;
    lookback_months?: number;
    min_bill_amount?: number;
    min_months_qualifying?: number;
    min_months_item?: number;
  }) => {
    const params = new URLSearchParams();
    params.append('store_id', String(filters.store_id));
    Object.entries(filters).forEach(([k, v]) => {
      if (k !== 'store_id' && v !== undefined && v !== null && !(typeof v === 'number' && Number.isNaN(v))) {
        params.append(k, String(v));
      }
    });
    return api.get(`/utilities/monthly-refills/${customerId}/items?${params}`).then(r => r.data);
  },

  getOrderReview: (payload: {
    store_id: number; customer_ids: number[];
    lookback_months?: number; min_bill_amount?: number;
    min_months_qualifying?: number; min_months_item?: number; sole_buyer_max?: number;
  }) => api.post('/utilities/monthly-refills/order-review', payload).then(r => r.data),

  markMessaged: (customerId: number, payload: {
    store_id: number; cycle_start: string; item_count?: number; estimated_amount?: number;
  }) => api.post(`/utilities/monthly-refills/${customerId}/mark-messaged`, payload).then(r => r.data),
};

// Inter-Store Stock Balancing
export const stockTransferAPI = {
  getRecommendations: (): Promise<any> =>
    api.get('/stock-transfers/recommendations').then(r => r.data),

  list: (filters?: { status?: string; store_id?: number }): Promise<any[]> => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.store_id) params.append('store_id', String(filters.store_id));
    const qs = params.toString();
    return api.get(`/stock-transfers${qs ? `?${qs}` : ''}`).then(r => r.data);
  },

  getById: (id: number): Promise<any> =>
    api.get(`/stock-transfers/${id}`).then(r => r.data),

  create: (payload: {
    from_store_id: number;
    to_store_id: number;
    notes?: string;
    items: Array<{
      manufacturer_id: number;
      suggested_qty: number;
      approved_qty?: number;
      landing_price?: number;
      source_stock?: number;
      source_velocity_90d?: number;
      dest_stock?: number;
      dest_velocity_90d?: number;
      reason?: string;
    }>;
  }): Promise<any> =>
    api.post('/stock-transfers', payload).then(r => r.data),

  updateStatus: (id: number, status: string): Promise<any> =>
    api.patch(`/stock-transfers/${id}/status`, { status }).then(r => r.data),

  // Returns the raw CSV blob — the endpoint is auth-gated (requires the x-user-email
  // header the axios interceptor attaches), so a plain <a href> link can't be used;
  // caller must build an object URL from the blob, matching PharmacistAudit.tsx's PDF
  // download pattern.
  downloadPicklistCsv: (id: number): Promise<Blob> =>
    api.get(`/stock-transfers/${id}/picklist.csv`, { responseType: 'blob' }).then(r => r.data),
};

// evitalrx Purchase Returns Tracker
export const evitalrxReturnsAPI = {
  sync: (storeId: number | 'all', fromDate?: string, toDate?: string) =>
    api.post('/utilities/evitalrx-returns-sync', {
      store_id: storeId,
      from_date: fromDate,
      to_date: toDate,
    }).then(r => r.data),

  getReturns: (filters: {
    store_id?: number | 'all';
    from_date?: string;
    to_date?: string;
    distributor_name?: string;
    status?: string;
  }) => {
    const params = new URLSearchParams();
    if (filters.store_id && filters.store_id !== 'all') params.append('store_id', String(filters.store_id));
    if (filters.from_date) params.append('from_date', filters.from_date);
    if (filters.to_date) params.append('to_date', filters.to_date);
    if (filters.distributor_name) params.append('distributor_name', filters.distributor_name);
    if (filters.status) params.append('status', filters.status);
    return api.get(`/utilities/evitalrx-purchase-returns?${params}`).then(r => r.data);
  },
};

// Customer Reports API
export const customerReportsAPI = {
  getCustomerReports: (customerNumber: number): Promise<any> => 
    api.get(`/utilities/customer-reports?customer_number=${customerNumber}`).then(response => response.data),
  
  getCustomerSales60d: (storeId: number, manufacturerIds: number[]): Promise<any> => 
    api.post('/utilities/customer-sales-60d', { store_id: storeId, manufacturer_ids: manufacturerIds }).then(response => response.data),
  
  getCustomersWithRefills: (storeId: number): Promise<any> => 
    api.get(`/utilities/customer-refills?store_id=${storeId}`).then(response => response.data),
  
  updateCustomerRefills: (customerNumber: number, storeId: number, medicines: any[]): Promise<any> => 
    api.post('/utilities/customer-refills/update', { customer_number: customerNumber, store_id: storeId, medicines }).then(response => response.data),
  
  removeCustomerRefills: (customerNumber: number): Promise<any> => 
    api.delete(`/utilities/customer-refills/${customerNumber}`).then(response => response.data),
};

export default api;

// Order Processor: evitalRx uploads → optimizer run → review → distributors → PO → receipt
const opQs = (params: Record<string, string | number | undefined | null>) => {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') p.append(k, String(v)); });
  const s = p.toString();
  return s ? `?${s}` : '';
};

const opUpload = (kind: 'sales' | 'purchase' | 'inventory', storeId: number, file: File, confirmStoreMismatch = false) => {
  const form = new FormData();
  form.append('store_id', String(storeId));
  if (confirmStoreMismatch) form.append('confirm_store_mismatch', 'true');
  form.append('file', file);
  return api.post(`/order-processor/upload/${kind}`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 300000,
  }).then(r => r.data);
};

export const orderProcessorAPI = {
  stores: (): Promise<Array<{ id: number; store_name: string }>> => api.get('/order-processor/stores').then(r => r.data),
  distributors: (): Promise<Array<{ id: number; distributor_name: string }>> => api.get('/order-processor/distributors').then(r => r.data),
  dataStatus: (): Promise<any[]> => api.get('/order-processor/data-status').then(r => r.data),

  uploadSales: (storeId: number, file: File, confirmStoreMismatch = false) => opUpload('sales', storeId, file, confirmStoreMismatch),
  uploadPurchase: (storeId: number, file: File, confirmStoreMismatch = false) => opUpload('purchase', storeId, file, confirmStoreMismatch),
  uploadInventory: (storeId: number, file: File, confirmStoreMismatch = false) => opUpload('inventory', storeId, file, confirmStoreMismatch),

  storeSources: (): Promise<Array<{ store_id: number; store_name: string; source: 'evitalrx' | 'pos'; pos_from: string | null; last_pos_sync_at: string | null; updated_by?: string; updated_at?: string; opened_on: string | null; new_store_until: string | null; new_store_active: boolean }>> =>
    api.get('/order-processor/store-sources').then(r => r.data),
  setStoreSource: (storeId: number, body: { source: 'evitalrx' | 'pos'; pos_from?: string | null; opened_on?: string | null }): Promise<any> =>
    api.put(`/order-processor/store-sources/${storeId}`, body).then(r => r.data),
  syncPos: (storeId: number, from?: string, to?: string): Promise<any> =>
    api.post('/order-processor/sync/pos', { store_id: storeId, from: from || undefined, to: to || undefined }, { timeout: 300000 }).then(r => r.data),

  listRuns: (f: { store_id?: number; status?: string; source?: string; from?: string; to?: string } = {}): Promise<any[]> =>
    api.get(`/order-processor/runs${opQs(f)}`).then(r => r.data),
  createOptimizerRun: (storeId: number, inventoryUploadId: number): Promise<any> =>
    api.post('/order-processor/runs', { store_id: storeId, inventory_upload_id: inventoryUploadId }, { timeout: 300000 }).then(r => r.data),
  createCommitmentRun: (storeId: number, dateFrom: string, dateTo: string): Promise<any> =>
    api.post('/order-processor/runs', { store_id: storeId, source: 'commitment', date_from: dateFrom, date_to: dateTo }).then(r => r.data),
  getRun: (id: number): Promise<any> => api.get(`/order-processor/runs/${id}`).then(r => r.data),
  addCustomerOrders: (id: number, dateFrom: string, dateTo: string): Promise<any> =>
    api.post(`/order-processor/runs/${id}/customer-orders`, { date_from: dateFrom, date_to: dateTo }).then(r => r.data),
  runEvents: (id: number): Promise<any[]> => api.get(`/order-processor/runs/${id}/events`).then(r => r.data),
  updateItems: (id: number, updates: Array<{ id: number; decision?: string; approved_qty?: number; reject_reason?: string }>) =>
    api.patch(`/order-processor/runs/${id}/items`, { updates }).then(r => r.data),
  setRunStatus: (id: number, status: string) => api.patch(`/order-processor/runs/${id}/status`, { status }).then(r => r.data),
  assignDistributors: (id: number, includeOtherZones = false) =>
    api.post(`/order-processor/runs/${id}/assign-distributors`, { include_other_zones: includeOtherZones }, { timeout: 300000 }).then(r => r.data),
  distributorZones: (): Promise<{ zones: Array<{ id: number; name: string }>; distributors: Array<{ distributor_id: number; distributor_name: string; city: string | null; pincode: string | null; state: string | null; zone_id: number; saved_zone_id: number | null; source: 'auto' | 'manual' | null; updated_by?: string; purchase_lines: number; last_purchase: string | null; stores: number[] | null }> }> =>
    api.get('/order-processor/distributor-zones').then(r => r.data),
  setDistributorZone: (distributorId: number, zoneId: number): Promise<any> =>
    api.put(`/order-processor/distributor-zones/${distributorId}`, { zone_id: zoneId }).then(r => r.data),
  overrideDistributor: (runId: number, itemId: number, distributorId: number) =>
    api.patch(`/order-processor/runs/${runId}/items/${itemId}/distributor`, { distributor_id: distributorId }).then(r => r.data),
  distributorHints: (id: number): Promise<{ tiers: Record<string, string>; hints: any[] }> =>
    api.get(`/order-processor/runs/${id}/distributor-hints`).then(r => r.data),
  createPurchaseOrders: (id: number) => api.post(`/order-processor/runs/${id}/purchase-orders`).then(r => r.data),
  exportRunCsv: (id: number, kind: 'suggestions' | 'remaining' | 'reviewed' | 'po-request'): Promise<Blob> =>
    api.get(`/order-processor/runs/${id}/export/${kind}.csv`, { responseType: 'blob' }).then(r => r.data),

  listPurchaseOrders: (f: { store_id?: number; status?: string; run_id?: number; distributor_id?: number; from?: string; to?: string } = {}): Promise<any[]> =>
    api.get(`/order-processor/purchase-orders${opQs(f)}`).then(r => r.data),
  getPurchaseOrder: (id: number): Promise<any> => api.get(`/order-processor/purchase-orders/${id}`).then(r => r.data),
  listPoLines: (f: { view?: 'optimizer' | 'customer' | 'all'; from?: string; to?: string; store_id?: number; status?: string; run_id?: number; include_cancelled?: boolean } = {}): Promise<any[]> =>
    api.get(`/order-processor/purchase-orders/lines${opQs(f as any)}`).then(r => r.data),
  getCustomerOrder: (id: number): Promise<any> => api.get(`/order-processor/customer-orders/${id}`).then(r => r.data),
  markCustomerOrderOrdered: (id: number): Promise<{ updated: number; ids: number[] }> =>
    api.post(`/order-processor/customer-orders/${id}/mark-ordered`).then(r => r.data),
  markPoCustomerOrdersOrdered: (poId: number): Promise<{ updated: number; ids: number[] }> =>
    api.post(`/order-processor/purchase-orders/${poId}/mark-customer-orders-ordered`).then(r => r.data),
  bulkPurchaseOrderStatus: (ids: number[], status: string): Promise<{ status: string; done: any[]; skipped: any[] }> =>
    api.post('/order-processor/purchase-orders/bulk-status', { ids, status }).then(r => r.data),
  setPurchaseOrderStatus: (id: number, status: string) => api.patch(`/order-processor/purchase-orders/${id}/status`, { status }).then(r => r.data),
  matchReceipts: (id: number) => api.post(`/order-processor/purchase-orders/${id}/match-receipts`).then(r => r.data),
  matchAllReceipts: (storeId?: number) => api.post('/order-processor/purchase-orders/match-receipts', { store_id: storeId }).then(r => r.data),
  overrideReceipt: (poId: number, itemId: number, body: { received_qty?: number; line_status?: string; notes?: string }) =>
    api.patch(`/order-processor/purchase-orders/${poId}/items/${itemId}`, body).then(r => r.data),
  reorderOptions: (id: number): Promise<any> => api.get(`/order-processor/purchase-orders/${id}/reorder-options`).then(r => r.data),
  reorderLines: (id: number, lines: Array<{ item_id: number; distributor_id: number; qty?: number }>): Promise<any[]> =>
    api.post(`/order-processor/purchase-orders/${id}/reorder`, { lines }).then(r => r.data),
  exportPoCsv: (id: number): Promise<Blob> => api.get(`/order-processor/purchase-orders/${id}/export.csv`, { responseType: 'blob' }).then(r => r.data),
  exportPoPdf: (id: number): Promise<Blob> => api.get(`/order-processor/purchase-orders/${id}/export.pdf`, { responseType: 'blob' }).then(r => r.data),

  events: (f: { from?: string; to?: string; store_id?: number; event_type?: string } = {}): Promise<any[]> =>
    api.get(`/order-processor/events${opQs(f)}`).then(r => r.data),
};
